import { useState } from 'react';
import { useSearchParamsWithApiKey, useDebouncedEffect } from '../../../tools';
import type { SelectorOption } from '../../common';

const CONTENT_TYPE = 'textGraphics';

export type EditImageContentType = typeof CONTENT_TYPE;

export const MODEL_OPTIONS: SelectorOption[] = [
    { label: 'Standard', value: 'standard' },
    { label: 'Premium', value: 'premium' },
];

interface UseEditImageParams {
    prompt: string;
    description: string;
    imageUrl: string;
    imageCount: number;
    modelSelectedOption: SelectorOption;
    setPrompt: (prompt: string) => void;
    setDescription: (description: string) => void;
    setImageUrl: (imageUrl: string) => void;
    setImageCount: (imageCount: number) => void;
    setModelSelectedOption: (modelOption: SelectorOption) => void;
}

export const useEditImageParams = (): UseEditImageParams => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();
    const promptParam = searchParams.get('prompt');
    const descriptionParam = searchParams.get('description');
    const imageUrlParam = searchParams.get('imageUrl');
    const imageCountParam = searchParams.get('imageCount');
    const modelParam = searchParams.get('modelSelectedOption') ?? searchParams.get('model');

    const [prompt, setPrompt] = useState<string>(promptParam ?? '');
    const [description, setDescription] = useState<string>(descriptionParam ?? '');
    const [imageUrl, setImageUrl] = useState<string>(imageUrlParam ?? '');
    const [imageCount, setImageCount] = useState<number>(imageCountParam ? parseInt(imageCountParam) : 1);
    const [modelSelectedOption, setModelSelectedOption] = useState<SelectorOption>(
        modelParam ? MODEL_OPTIONS.find((option) => option.value === modelParam) || MODEL_OPTIONS[0] : MODEL_OPTIONS[0],
    );

    useDebouncedEffect(
        () => {
            const searchParamsObj: Record<string, string | string[]> = {};

            if (prompt) {
                searchParamsObj['prompt'] = prompt;
            }
            if (description) {
                searchParamsObj['description'] = description;
            }
            if (imageUrl) {
                searchParamsObj['imageUrl'] = imageUrl;
            }
            if (imageCount) {
                searchParamsObj['imageCount'] = imageCount.toString();
            }
            if (modelSelectedOption.value) {
                searchParamsObj['modelSelectedOption'] = modelSelectedOption.value;
            }
            setSearchParams(searchParamsObj);
        },
        [prompt, description, imageUrl, imageCount, modelSelectedOption, setSearchParams],
        500,
    );

    return {
        prompt,
        description,
        imageUrl,
        imageCount,
        modelSelectedOption,
        setPrompt,
        setDescription,
        setImageUrl,
        setImageCount,
        setModelSelectedOption,
    };
};
